import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { attachClosePhotos, listRecentCloses } from "@/lib/api";
import { fileToCompressedBase64 } from "@/components/PhotoStep";
import { TILL_LABEL } from "@/lib/constants";
import { formatDate } from "@/lib/format";
import { AlertTriangle, Camera, CheckCircle2, ChevronLeft, ChevronRight, ImagePlus, X as XIcon } from "lucide-react";
import { toast } from "sonner";

export default function AttachPhotos() {
  const nav = useNavigate();
  const [closes, setCloses] = useState(null);
  const [selected, setSelected] = useState(null);
  const [moneris, setMoneris] = useState(null);
  const [register, setRegister] = useState(null);
  const [extras, setExtras] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    listRecentCloses()
      .then(setCloses)
      .catch(() => { setCloses([]); toast.error("Couldn't load recent closes"); });
  }, []);

  const reset = () => { setSelected(null); setMoneris(null); setRegister(null); setExtras([]); setResult(null); };

  const save = async () => {
    if (!moneris && !register && extras.length === 0) { toast.error("Add at least one photo first"); return; }
    setSaving(true);
    try {
      const data = await attachClosePhotos(selected.id, {
        moneris_photo_b64: moneris,
        register_photo_b64: register,
        extra_photos_b64: extras,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save photos — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
      <Header title="Add receipt photos" onBack={() => (selected && !result ? setSelected(null) : nav("/"))} />
      <main className="max-w-md mx-auto px-5 pb-14 pt-6 space-y-4" data-testid="attach-photos-page">
        {result ? (
          <ResultCard result={result} selected={selected} onDone={() => nav("/")} onView={() => nav(`/close-detail/${selected.id}`)} onMore={reset} />
        ) : !selected ? (
          <>
            <div>
              <div className="text-[10px] tracking-[0.22em] text-[#5C7566] uppercase">Step 1 of 2</div>
              <h2 className="text-3xl text-[#0D311E] mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
                Which close are the pics for?
              </h2>
              <p className="text-[#2B533E] mt-2 text-sm">Pick the close — the photos get saved onto it and the AI double-checks the numbers.</p>
            </div>
            {closes === null && <div className="text-center text-[#5C7566] py-8">Loading…</div>}
            {closes !== null && closes.length === 0 && (
              <div className="text-center text-[#5C7566] py-8" data-testid="attach-empty">No closes yet — finish a close first.</div>
            )}
            {(closes || []).map((cl) => {
              const count = (cl.has_moneris_photo ? 1 : 0) + (cl.has_register_photo ? 1 : 0) + (cl.extra_photo_count || 0);
              return (
                <button
                  key={cl.id}
                  data-testid={`attach-pick-${cl.id}`}
                  onClick={() => setSelected(cl)}
                  className="w-full bg-white rounded-2xl border border-[#E8E4DA] p-4 text-left hover:border-[#1A5D3A] active:scale-[0.99] transition-all flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0D311E] font-medium truncate">
                      {TILL_LABEL[cl.till] || cl.till} · {String(cl.mode).replace(/_/g, " ")}
                    </div>
                    <div className="text-xs text-[#5C7566] truncate">
                      {formatDate(cl.created_at)}{cl.who_worked ? ` · ${cl.who_worked}` : ""}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] shrink-0 ${count === 0 ? "bg-[#D97706]/15 text-[#92520A]" : "bg-[#F5F2EA] text-[#5C7566]"}`}>
                    {count === 0 ? "No photos yet" : `${count} photo${count > 1 ? "s" : ""}`}
                  </span>
                  <ChevronRight size={16} className="text-[#5C7566] shrink-0" />
                </button>
              );
            })}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-[#2B533E]">
              <button data-testid="attach-change-close" onClick={() => setSelected(null)} className="flex items-center gap-1 hover:underline">
                <ChevronLeft size={14} /> Change close
              </button>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] text-[#5C7566] uppercase">Step 2 of 2</div>
              <h2 className="text-2xl text-[#0D311E] mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
                {TILL_LABEL[selected.till] || selected.till} · {String(selected.mode).replace(/_/g, " ")}
              </h2>
              <p className="text-[#5C7566] text-sm">{formatDate(selected.created_at)}</p>
            </div>

            <PhotoSlot
              label="Moneris receipt"
              hint="The card terminal daily summary"
              value={moneris}
              onChange={setMoneris}
              testId="attach-moneris"
            />
            <PhotoSlot
              label="Register tape"
              hint="The Z report from the register"
              value={register}
              onChange={setRegister}
              testId="attach-register"
            />
            <ExtraSlot extras={extras} setExtras={setExtras} />

            <button
              data-testid="attach-save"
              onClick={save}
              disabled={saving}
              className="w-full h-14 rounded-2xl bg-[#1A5D3A] text-white font-medium flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-60"
            >
              {saving ? "Saving & checking the numbers…" : "Save photos & check numbers"}
            </button>
            {saving && (
              <div className="text-center text-[#5C7566] text-sm">The AI is reading the receipts — takes a few seconds.</div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function PhotoSlot({ label, hint, value, onChange, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { onChange(await fileToCompressedBase64(f)); }
    catch { toast.error("Couldn't read that image"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E8E4DA] p-4">
      <input ref={ref} data-testid={`${testId}-input`} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative shrink-0">
            <img src={value} alt={label} className="h-16 w-16 object-cover rounded-lg border border-[#E8E4DA] bg-[#F5F2EA]" />
            <button
              data-testid={`${testId}-remove`}
              onClick={() => onChange(null)}
              aria-label="Remove"
              className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#0D311E] text-white flex items-center justify-center"
            >
              <XIcon size={10} />
            </button>
          </div>
        ) : (
          <div className="h-16 w-16 rounded-lg bg-[#F5F2EA] border border-dashed border-[#1A5D3A]/40 flex items-center justify-center text-[#1A5D3A] shrink-0">
            <Camera size={20} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[#0D311E] font-medium">{label}</div>
          <div className="text-xs text-[#5C7566]">{hint}</div>
        </div>
        <button
          data-testid={`${testId}-add`}
          onClick={() => ref.current?.click()}
          disabled={busy}
          className="h-10 px-4 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm shrink-0 disabled:opacity-50"
        >
          {busy ? "…" : value ? "Retake" : "Add"}
        </button>
      </div>
    </div>
  );
}

function ExtraSlot({ extras, setExtras }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const encoded = await Promise.all(files.map((f) => fileToCompressedBase64(f)));
      setExtras((prev) => [...prev, ...encoded]);
    } catch { toast.error("Couldn't read those images"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E8E4DA] p-4">
      <input ref={ref} data-testid="attach-extra-input" type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onFiles} />
      <div className="flex items-center gap-3">
        <div className="h-16 w-16 rounded-lg bg-[#F5F2EA] border border-dashed border-[#1A5D3A]/40 flex items-center justify-center text-[#1A5D3A] shrink-0">
          <ImagePlus size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[#0D311E] font-medium">Other photos</div>
          <div className="text-xs text-[#5C7566]">Daily sheet, tape sections, anything else</div>
        </div>
        <button
          data-testid="attach-extra-add"
          onClick={() => ref.current?.click()}
          disabled={busy}
          className="h-10 px-4 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm shrink-0 disabled:opacity-50"
        >
          {busy ? "…" : "Add"}
        </button>
      </div>
      {extras.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mt-3" data-testid="attach-extra-thumbs">
          {extras.map((src, i) => (
            <div key={i} className="relative">
              <img src={src} alt={`Extra ${i + 1}`} className="w-full h-16 object-cover rounded-lg border border-[#E8E4DA] bg-[#F5F2EA]" />
              <button
                data-testid={`attach-extra-remove-${i}`}
                onClick={() => setExtras((prev) => prev.filter((_, x) => x !== i))}
                aria-label="Remove"
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#0D311E] text-white flex items-center justify-center"
              >
                <XIcon size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultCard({ result, onDone, onView, onMore }) {
  const bad = result.mismatch;
  return (
    <div className="space-y-4" data-testid="photo-check-result">
      <div className={`rounded-2xl p-5 border ${bad ? "bg-[#B91C1C]/10 border-[#B91C1C]/30" : "bg-[#1B7B4B]/10 border-[#1B7B4B]/30"}`}>
        <div className="flex items-center gap-2">
          {bad ? <AlertTriangle size={20} className="text-[#B91C1C]" /> : <CheckCircle2 size={20} className="text-[#1B7B4B]" />}
          <div className="text-lg text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>
            {bad ? "Numbers don't quite match" : "Photos saved"}
          </div>
        </div>
        {result.summary && (
          <div className="whitespace-pre-wrap text-sm text-[#2B533E] mt-3" data-testid="photo-check-summary-text">{result.summary}</div>
        )}
        {bad && (
          <div className="text-xs text-[#7F1D1D] mt-3">This close has been flagged so the owner takes a look. Nobody's in trouble — it's just a heads-up.</div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="attach-view-close" onClick={onView} className="h-12 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm">
          View this close
        </button>
        <button data-testid="attach-done" onClick={onDone} className="h-12 rounded-xl bg-[#1A5D3A] text-white text-sm">
          Done
        </button>
      </div>
      <button data-testid="attach-more" onClick={onMore} className="w-full h-11 rounded-xl border border-dashed border-[#E8E4DA] text-[#2B533E] text-sm">
        Add photos to another close
      </button>
    </div>
  );
}
